import t from "@ext/localization/locale/translate";
import { Popover, PopoverTrigger } from "@ui-kit/Popover";
import { type MouseEvent, useState } from "react";
import { type ConfirmOptions, ConfirmPopoverContent } from "./ConfirmPopoverContent";

export type ConfirmPopoverProps = ConfirmOptions & {
	children: JSX.Element;
	disabled?: boolean;
	onConfirm: () => unknown;
	onOpenChange?: (open: boolean) => void;
};

export const confirmSkipHotKey = () => `Shift-${t("confirm-popover.click")}`;

export const ConfirmPopover = (props: ConfirmPopoverProps) => {
	const { children, disabled, onConfirm, onOpenChange, ...options } = props;

	const [open, setOpen] = useState(false);
	const [isPending, setIsPending] = useState(false);

	const changeOpen = (next: boolean) => {
		if (isPending) return;
		setOpen(next);
		onOpenChange?.(next);
	};

	const confirm = async () => {
		setIsPending(true);
		try {
			await onConfirm();
		} finally {
			setIsPending(false);
			setOpen(false);
			onOpenChange?.(false);
		}
	};

	const onTriggerClick = (event: MouseEvent) => {
		if (!event.shiftKey) return;
		event.preventDefault();
		if (!isPending) void confirm();
	};

	if (disabled) return children;

	return (
		<Popover onOpenChange={changeOpen} open={open}>
			<PopoverTrigger asChild onClick={onTriggerClick}>
				{children}
			</PopoverTrigger>
			<ConfirmPopoverContent
				{...options}
				isPending={isPending}
				onCancel={() => changeOpen(false)}
				onConfirm={() => void confirm()}
			/>
		</Popover>
	);
};
